import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useTheme } from '../context/ThemeContext';
import authService from '../services/api/authService';
import Toast from '../components/Toast/Toast';
import { getPasswordErrors } from '../utils/validation';
import PasswordRequirements from '../components/common/PasswordRequirements';

const ChangePassword: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
  } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'warning') => {
    setToast({ message, type });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      showToast('All fields are required', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }

    if (currentPassword === newPassword) {
      showToast('New password must be different from current password', 'error');
      return;
    }

    if (getPasswordErrors(newPassword).length > 0) {
      showToast('Please meet all the password requirements listed below.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      showToast('Password changed successfully!', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1500);
    } catch (error: any) {
      let errorMessage = 'Something went wrong. Please try again.';

      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMessage = 'Current password is incorrect.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak. Please use a stronger password.';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please log in again before changing your password.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="Change Password">
      <div className="max-w-md mx-auto">
        <p className="mb-8 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base" style={{ color: theme.textSecondary }}>
          Update your password without leaving the current theme experience.
        </p>
        <form
          onSubmit={handleSubmit}
          className="space-y-6 p-6 rounded-2xl border shadow-sm"
          style={{ backgroundColor: theme.surface, borderColor: theme.border }}
        >
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
              Current Password
            </label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none"
              style={{
                backgroundColor: theme.surface,
                color: theme.text,
                borderColor: theme.border,
                boxShadow: 'none',
              }}
              required
            />
          </div>
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none"
              style={{
                backgroundColor: theme.surface,
                color: theme.text,
                borderColor: theme.border,
                boxShadow: 'none',
              }}
              required
            />
            <PasswordRequirements
              password={newPassword}
              className="mt-2"
              containerStyle={{ backgroundColor: theme.surface, borderColor: theme.border }}
              labelColor={theme.textSecondary}
            />
          </div>
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none"
              style={{
                backgroundColor: theme.surface,
                color: theme.text,
                borderColor: theme.border,
                boxShadow: 'none',
              }}
              required
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-2 px-4 rounded-md focus:outline-none transition-colors disabled:opacity-60"
            style={{ backgroundColor: theme.primary, color: theme.contrastText }}
          >
            {isSubmitting ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>
      </div>
      {toast && (
        <>
          <p
            className="sr-only"
            role={toast.type === 'error' ? 'alert' : 'status'}
            aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
            aria-atomic="true"
          >
            {toast.message}
          </p>
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        </>
      )}
    </DashboardLayout>
  );
};

export default ChangePassword;
